import { type NuxtAxiosInstance } from "@nuxtjs/axios";

import { type Settings, type SettingsApi, type SettingsResponse } from "@/types";
import { useApi } from "@/utils/api";

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * @function useSettingsApi
 *
 * @desc        A composable that exposes functions used to interact with the
 *              `consumerProfileService API`. The functions are wrappers around
 *              the useApi composable's request function. The returned functions
 *              can be destructured without losing any functionality.
 *
 * @summary     A composable used to interact with the consumerProfileService API.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @returns {SettingsApi} Object for interacting with `consumerProfileService API`; see {@link SettingsApi}.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @example <caption>Exposed actions of `useSettingsApi`</caption>
 * ```ts
 *   const _api = useSettingsApi();
 *   const { get, put } = _api;
 *
 *   const { data: settings } = await get();
 *   const { data: updatedSettings } = await put({ email: "" });
 * ```
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

export default function useSettingsApi($axios: NuxtAxiosInstance): SettingsApi {
  const _api = useApi({ name: "consumerProfileService", url: `/v2/settings${process.client ? "/" : ""}`, $axios });

  async function get() {
    return await _api.request<SettingsResponse>({
      method: "GET",
    });
  }

  async function put(data: Partial<Settings>) {
    return await _api.request<SettingsResponse>({
      method: "PUT",
      data,
    });
  }

  return { get, put };
}
